import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useDashboardStore from '../../stores/dashboardStore';

// Usage:
// <MatchingSettings allFieldsDone={allFieldsDone} />

const MatchingSettings = ({ allFieldsDone }) => {
    const { t, i18n } = useTranslation();

    const formData = useDashboardStore(s => s.formData);
    const setFormData = useDashboardStore(s => s.setFormData);
    const autoSaveProfile = useDashboardStore(s => s.autoSaveProfile);
    const savedSections = useDashboardStore(s => s.savedSections);
    const communities = useDashboardStore(s => s.communities);
    const matchingContext = useDashboardStore(s => s.matchingContext);
    const handleMatchingContextChange = useDashboardStore(s => s.handleMatchingContextChange);

    const getNextWeekDateRange = () => {
        const today = new Date();
        const nextMonday = new Date(today);
        nextMonday.setDate(today.getDate() + ((1 + 7 - today.getDay()) % 7 || 7));

        const nextSunday = new Date(nextMonday);
        nextSunday.setDate(nextMonday.getDate() + 6);

        const options = { month: 'short', day: 'numeric' };
        const start = nextMonday.toLocaleDateString(i18n.language === 'ru' ? 'ru-RU' : 'en-US', options);
        const end = nextSunday.toLocaleDateString(i18n.language === 'ru' ? 'ru-RU' : 'en-US', options);

        return `(${start} — ${end})`;
    };

    const activeCommunities = communities.filter(c => c.status === 'Active');

    return (
        <div className="glass-card" style={{
            padding: '1.5rem',
            opacity: allFieldsDone ? 1 : 0.6,
            filter: allFieldsDone ? 'none' : 'blur(4px)',
            pointerEvents: allFieldsDone ? 'auto' : 'none',
            transition: 'all 0.3s ease'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 className="section-title" style={{ marginBottom: 0 }}>{t('dashboard.matching.title', 'Matching Settings')}</h2>
            </div>

            {/* Next Week Status Switch */}
            <div className="input-group" style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', minHeight: '27px' }}>
                    <label className="form-label" style={{ textAlign: 'left', margin: 0, display: 'flex', alignItems: 'center' }}>
                        {t('dashboard.matching.next_week_status', 'Next week')}
                        {savedSections['nextWeekStatus'] ? (
                            <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.9em', marginLeft: '0.5rem', fontWeight: 'normal' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                {t('dashboard.profile.saved', 'Saved')}
                            </span>
                        ) : (
                            <span style={{ fontWeight: 'normal', fontSize: '0.9em', color: '#666', marginLeft: '0.5rem' }}>{getNextWeekDateRange()}</span>
                        )}
                    </label>
                </div>

                <div
                    style={{
                        display: 'flex',
                        background: '#f3f4f6',
                        borderRadius: '0.5rem',
                        padding: '0.25rem',
                        cursor: 'pointer',
                        position: 'relative'
                    }}
                >
                    <div
                        onClick={() => {
                            const newState = 'Active';
                            setFormData(prev => ({ ...prev, nextWeekStatus: newState }));
                            autoSaveProfile({ ...formData, nextWeekStatus: newState }, 'nextWeekStatus');
                        }}
                        style={{
                            flex: 1,
                            padding: '0.5rem',
                            textAlign: 'center',
                            borderRadius: '0.375rem',
                            background: formData.nextWeekStatus === 'Active' ? '#fff' : 'transparent',
                            boxShadow: formData.nextWeekStatus === 'Active' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            color: formData.nextWeekStatus === 'Active' ? '#10b981' : '#6b7280',
                            fontWeight: formData.nextWeekStatus === 'Active' ? '600' : '400',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem'
                        }}
                    >
                        {t('dashboard.matching.status_active', "I'm in")} <span style={{ filter: formData.nextWeekStatus === 'Active' ? 'none' : 'grayscale(100%) opacity(0.5)' }}>✅</span>
                    </div>
                    <div
                        onClick={() => {
                            const newState = 'Passive';
                            setFormData(prev => ({ ...prev, nextWeekStatus: newState }));
                            autoSaveProfile({ ...formData, nextWeekStatus: newState }, 'nextWeekStatus');
                        }}
                        style={{
                            flex: 1,
                            padding: '0.5rem',
                            textAlign: 'center',
                            borderRadius: '0.375rem',
                            background: formData.nextWeekStatus === 'Passive' ? '#fff' : 'transparent',
                            boxShadow: formData.nextWeekStatus === 'Passive' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            color: formData.nextWeekStatus === 'Passive' ? '#ef4444' : '#6b7280',
                            fontWeight: formData.nextWeekStatus === 'Passive' ? '600' : '400',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem'
                        }}
                    >
                        {t('dashboard.matching.status_passive', "I'll skip")} <span style={{ filter: formData.nextWeekStatus === 'Passive' ? 'none' : 'grayscale(100%) opacity(0.5)' }}>❌</span>
                    </div>
                </div>
            </div>

            {/* Pause all invitations */}
            <div className="input-group" style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label className="form-label" style={{ textAlign: 'left', margin: 0, display: 'flex', alignItems: 'center' }}>
                        {t('dashboard.matching.pause_label', "Don't match me")} 🔕
                        {savedSections['notificationsPaused'] && (
                            <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.9em', marginLeft: '0.5rem', fontWeight: 'normal' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                {t('dashboard.profile.saved', 'Saved')}
                            </span>
                        )}
                    </label>
                    <div
                        onClick={() => {
                            // Same two writes as the bot: 🔕 = paused + Passive,
                            // /resume = unpaused + Active. Keep the switch symmetric.
                            const paused = !formData.notificationsPaused;
                            const newData = paused
                                ? { ...formData, notificationsPaused: true, nextWeekStatus: 'Passive' }
                                : { ...formData, notificationsPaused: false, nextWeekStatus: 'Active' };
                            setFormData(newData);
                            autoSaveProfile(newData, 'notificationsPaused');
                        }}
                        style={{
                            width: '44px', height: '24px', borderRadius: '12px', flexShrink: 0,
                            background: formData.notificationsPaused ? '#f59e0b' : '#e5e7eb',
                            position: 'relative', cursor: 'pointer', transition: 'background 0.2s'
                        }}
                    >
                        <div style={{
                            position: 'absolute', top: '2px',
                            left: formData.notificationsPaused ? '22px' : '2px',
                            width: '20px', height: '20px', borderRadius: '50%',
                            background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            transition: 'left 0.2s'
                        }} />
                    </div>
                </div>
                <p style={{ fontSize: '0.8rem', color: '#888', margin: '0.5rem 0 0', lineHeight: '1.4', textAlign: 'left' }}>
                    {formData.notificationsPaused
                        ? t('dashboard.matching.pause_hint_on', 'Paused: no matches and no Sunday invitations — same as the 🔕 button in the bot. Turn the switch off (or send the bot /resume) to come back.')
                        : t('dashboard.matching.pause_hint_off', 'Turn it on to leave matching and stop the Sunday invitations (same as the 🔕 button in the bot). Come back anytime here or with /resume in the bot.')}
                </p>
            </div>

            {/* My Communities — matching pool selector */}
            {activeCommunities.length > 0 && (
                <div className="input-group" style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <label className="form-label" style={{ textAlign: 'left', margin: 0 }}>
                            {t('community.my_title')}
                        </label>
                        {savedSections['matchingContext'] && (
                            <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.8rem' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                {t('dashboard.profile.saved', 'Saved')}
                            </span>
                        )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {/* Global option */}
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: '0.625rem 0.75rem',
                            borderRadius: '0.5rem',
                            backgroundColor: matchingContext === 'global' ? 'rgba(99,102,241,0.08)' : '#f8f8fa',
                            border: matchingContext === 'global' ? '2px solid #6366f1' : '1px solid rgba(0,0,0,0.06)',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}>
                            <input
                                type="radio"
                                name="matchingContext"
                                checked={matchingContext === 'global'}
                                onChange={() => handleMatchingContextChange('global')}
                                style={{ accentColor: '#6366f1', width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                            <span style={{ fontWeight: '600', fontSize: '0.9rem', color: '#333' }}>
                                {t('community.global_pool')}
                            </span>
                        </label>

                        {/* Community options */}
                        {activeCommunities.map(community => (
                            <label key={community.slug} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                padding: '0.625rem 0.75rem',
                                borderRadius: '0.5rem',
                                backgroundColor: matchingContext === `community:${community.slug}` ? 'rgba(99,102,241,0.08)' : '#f8f8fa',
                                border: matchingContext === `community:${community.slug}` ? '2px solid #6366f1' : '1px solid rgba(0,0,0,0.06)',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}>
                                <input
                                    type="radio"
                                    name="matchingContext"
                                    checked={matchingContext === `community:${community.slug}`}
                                    onChange={() => handleMatchingContextChange(`community:${community.slug}`)}
                                    style={{ accentColor: '#6366f1', width: '16px', height: '16px', cursor: 'pointer' }}
                                />
                                <Link
                                    to={`/community/${community.slug}`}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                        fontWeight: '600',
                                        fontSize: '0.9rem',
                                        color: '#6366f1',
                                        textDecoration: 'none'
                                    }}
                                    onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                                    onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                                >
                                    {community.name}
                                </Link>
                            </label>
                        ))}
                    </div>
                    <p style={{ fontSize: '0.8rem', color: '#888', margin: '0.5rem 0 0', lineHeight: '1.4' }}>
                        {matchingContext === 'global'
                            ? t('community.matching_hint_global')
                            : t('community.matching_hint_community')}
                    </p>
                </div>
            )}

            {/* Serendipity slider */}
            <div className="input-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <label className="form-label" style={{ textAlign: 'left', margin: 0 }}>
                        {t('dashboard.matching.serendipity', 'Serendipity Level')}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {savedSections['serendipity'] && (
                            <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.8rem' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                {t('dashboard.profile.saved', 'Saved')}
                            </span>
                        )}
                        <span style={{ color: '#7c3aed', fontWeight: 'bold' }}>{formData.serendipity}</span>
                    </div>
                </div>

                <input
                    type="range"
                    min="1"
                    max="10"
                    value={formData.serendipity}
                    onChange={(e) => setFormData({ ...formData, serendipity: parseInt(e.target.value) })}
                    onMouseUp={() => autoSaveProfile({ ...formData }, 'serendipity')}
                    onTouchEnd={() => autoSaveProfile({ ...formData }, 'serendipity')}
                    style={{
                        width: '100%',
                        accentColor: '#7c3aed',
                        height: '6px',
                        borderRadius: '3px',
                        marginBottom: '0.5rem',
                        cursor: 'pointer'
                    }}
                />

                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#666' }}>
                    <span>{t('dashboard.matching.low', 'Low (like me)')}</span>
                    <span>{t('dashboard.matching.high', 'High')}</span>
                </div>
            </div>

            {/* Proximity slider */}
            <div className="input-group" style={{ marginTop: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <label className="form-label" style={{ textAlign: 'left', margin: 0 }}>
                        {t('dashboard.matching.geography', 'Geography')}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {savedSections['proximity'] && (
                            <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.8rem' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                {t('dashboard.profile.saved', 'Saved')}
                            </span>
                        )}
                        <span style={{ color: '#7c3aed', fontWeight: 'bold' }}>{formData.proximity}</span>
                    </div>
                </div>

                <input
                    type="range"
                    min="1"
                    max="10"
                    value={formData.proximity}
                    onChange={(e) => setFormData({ ...formData, proximity: parseInt(e.target.value) })}
                    onMouseUp={() => autoSaveProfile({ ...formData }, 'proximity')}
                    onTouchEnd={() => autoSaveProfile({ ...formData }, 'proximity')}
                    style={{
                        width: '100%',
                        accentColor: '#7c3aed',
                        height: '6px',
                        borderRadius: '3px',
                        marginBottom: '0.5rem',
                        cursor: 'pointer'
                    }}
                />

                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#666' }}>
                    <span>{t('dashboard.matching.near_me', 'Near me')}</span>
                    <span>{t('dashboard.matching.far_away', 'Far away')}</span>
                </div>
            </div>
        </div>
    );
};

export default MatchingSettings;
